import { useState, useMemo, useEffect } from 'react'
import {
  RadioConfigFrame,
  InformationFrame,
  StyledWLANForm,
  WLANFormBlock,
  WLANFormBlockText,
  WLANFormBlockTitle,
  WLANFormInfoBlock,
  WLANFormInfoBlockTop,
  NavigateToMistButton,
} from './styles'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { CommonStyledInput } from 'components/Input/styles'
import FullscreenModal from 'components/Modal/FullscreenModal'
import { Dropdown, Checkbox } from 'components/Input'
import { DropdownOption } from 'components/Input/Dropdown'
import { orgRFTemplatesFetchDataSelector } from 'features/networkAi/selectors'

import {
  fetchOrgRFTemplateThunk,
  updateOrgRFTemplateThunk,
  optimizeRadiosThunk,
} from 'features/networkAi/slice'
import { SpinnerCentered } from 'components/Loader'
import { UpdateOrgRFTemplate, OptimizeRadios } from 'features/networkAi/types'

interface NetworkRadioConfigModalProps {
  isOpen: boolean
  orgId: string
  siteId: string
  rfTemplateID: string
  handleClose: VoidFunction
}

const SIX_GHZ_OPTIONS = [20, 40, 80, 160]
const FIVE_GHZ_OPTIONS = [20, 40, 80]

const RangeSixOptions = SIX_GHZ_OPTIONS.map((option) => ({
  label: `${option} MHz`,
  value: option,
}))

const RangeFiveOptions = FIVE_GHZ_OPTIONS.map((option) => ({
  label: `${option} MHz`,
  value: option,
}))

function NetworkRadioConfigModal({
  isOpen,
  orgId,
  siteId,
  rfTemplateID,
  handleClose,
}: NetworkRadioConfigModalProps) {
  const dispatch = useAppDispatch()

  const orgRFTemplates = useAppSelector(orgRFTemplatesFetchDataSelector.data)
  const isLoading = useAppSelector(orgRFTemplatesFetchDataSelector.isFetching)

  const [fiveGHzBandwith, setFiveGHzBandwith] = useState(40)
  const [sixGHzBandwith, setSixGHzBandwith] = useState(40)
  const [enableDFSChannels, setEnableDFSChannels] = useState(false)

  useEffect(() => {
    orgId && dispatch(fetchOrgRFTemplateThunk(orgId, rfTemplateID))
  }, [dispatch, orgId, rfTemplateID])

  const handleChangeRadio = async () => {
    if (orgRFTemplates != null) {
      const newBandwithValues: UpdateOrgRFTemplate = {
        band_5: {
          channels: null,
          bandwidth: fiveGHzBandwith,
        },
        band_6: {
          channels: null,
          bandwidth: sixGHzBandwith,
        },
      }

      const dfs_channels = [36, 40, 44, 48, 149, 153, 157, 161]

      if (enableDFSChannels) {
        newBandwithValues.band_5.channels = dfs_channels
      }

      const optimizeBody: OptimizeRadios = {
        bands: ['24', '5', '6'],
        auto_apply: true,
      }

      await dispatch(
        updateOrgRFTemplateThunk(orgId, rfTemplateID, newBandwithValues)
      )
      await dispatch(fetchOrgRFTemplateThunk(orgId, rfTemplateID))
      await dispatch(optimizeRadiosThunk(siteId, optimizeBody))
      handleClose()
    }
  }

  useEffect(() => {
    try {
      if (!isLoading && orgRFTemplates !== null) {
        setFiveGHzBandwith(orgRFTemplates.band_5.bandwidth)
        setSixGHzBandwith(orgRFTemplates.band_6.bandwidth)
        if (orgRFTemplates.band_5.channels !== null) {
          setEnableDFSChannels(true)
        }
      }
    } catch (error) {}
  }, [dispatch, isLoading, orgRFTemplates, setFiveGHzBandwith, orgId])

  const valuefiveGHz = useMemo(
    () =>
      RangeFiveOptions.find((option) => option.value === fiveGHzBandwith) || {
        label: `${fiveGHzBandwith} MHz`,
        value: fiveGHzBandwith,
      },
    [fiveGHzBandwith]
  )

  const valuesixGHz = useMemo(
    () =>
      RangeFiveOptions.find((option) => option.value === sixGHzBandwith) || {
        label: `${sixGHzBandwith} MHz`,
        value: sixGHzBandwith,
      },
    [sixGHzBandwith]
  )

  return (
    <FullscreenModal
      isOpen={isOpen}
      onClose={handleClose}
      headerTitle="Radio Configuration"
      footerCloseText="Cancel"
      footerConfirmText="Save"
      onConfirm={handleChangeRadio}
      disabledConfirm={false}
    >
      {isLoading ? (
        <SpinnerCentered mt="200px" />
      ) : (
        <RadioConfigFrame>
          <InformationFrame>
            <WLANFormInfoBlockTop>
              The following radio channel settings are recommended by Savant,
              and are assigned by default to all radios on site.
            </WLANFormInfoBlockTop>
            <WLANFormInfoBlockTop>2.4 GHz - 20 MHz</WLANFormInfoBlockTop>
            <WLANFormInfoBlock>5GHz - 40 MHz</WLANFormInfoBlock>
            <WLANFormInfoBlock>6GHz - 80 MHz</WLANFormInfoBlock>
            <WLANFormInfoBlockTop>
              Once Radio Channel settings have been saved, Savant Central
              Management will automatically apply the changes, and Marvis will
              optimize all site radios immediately.
            </WLANFormInfoBlockTop>

            <WLANFormInfoBlockTop />

            <WLANFormInfoBlockTop>
              <NavigateToMistButton
                as="a"
                href="https://www.mist.com/documentation/mesh-configuration/"
                target="_blank"
              >
                Learn How to Configure Mesh
              </NavigateToMistButton>
            </WLANFormInfoBlockTop>
          </InformationFrame>

          <StyledWLANForm>
            <WLANFormBlockTitle>2.4 GHz</WLANFormBlockTitle>
            <WLANFormBlockText>
              <CommonStyledInput type="text" value="20 MHz" disabled />
            </WLANFormBlockText>

            <WLANFormBlockTitle>5 GHz</WLANFormBlockTitle>
            <WLANFormBlock>
              <Dropdown
                options={RangeFiveOptions}
                name="band_5_bandwidth"
                value={valuefiveGHz}
                onChange={(option: DropdownOption) =>
                  setFiveGHzBandwith(Number(option.value))
                }
              />
            </WLANFormBlock>

            <WLANFormBlockTitle>6 GHz</WLANFormBlockTitle>
            <WLANFormBlock>
              <Dropdown
                options={RangeSixOptions}
                name="band_6_bandwidth"
                value={valuesixGHz}
                onChange={(option: DropdownOption) =>
                  setSixGHzBandwith(Number(option.value))
                }
              />
            </WLANFormBlock>

            <WLANFormBlockTitle></WLANFormBlockTitle>

            <WLANFormBlockTitle></WLANFormBlockTitle>
            <WLANFormBlock>
              <Checkbox
                name="dfs_enable"
                labelText="Enable Mesh"
                onChange={(e) => setEnableDFSChannels(e.target.checked)}
                checked={enableDFSChannels}
              />
            </WLANFormBlock>
          </StyledWLANForm>
        </RadioConfigFrame>
      )}
    </FullscreenModal>
  )
}

export default NetworkRadioConfigModal
